import { useQuery, useMutation, type UseQueryOptions, type UseMutationOptions, type UseQueryResult, type UseMutationResult } from "@tanstack/react-query";
import { customInstance, ErrorType } from "../axios-instance";
import * as api from "./client";
import { API_CONFIG } from "@/constants/api";
import type {
  TransMasterDto,
  SupplementDataDto,
  CampaignRequestDto,
  GetAllSupplementDataParams,
  GetAllCampaignsParams,
} from "./types";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

// Query Key Helpers
export const getGetAllSupplementDataQueryKey = (params?: GetAllSupplementDataParams) => {
  return [`/api/user/supp-data`, ...(params ? [params] : [])] as const;
};

export const getGetAllCampaignsQueryKey = (params?: GetAllCampaignsParams) => {
  return [`/api/user/campaign`, ...(params ? [params] : [])] as const;
};

export const getGetTransMasterByIdQueryKey = (id?: number) => {
  return [`/api/user/trans-master/${id}`] as const;
};

export const getGetCampaignByIdQueryKey = (id?: number) => {
  return [`/api/user/campaign/${id}`] as const;
};

// Trans Master Hooks
export const useGetTransMasterById = <
  TData = Awaited<ReturnType<typeof api.getTransMasterById>>,
  TError = ErrorType<unknown>
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getTransMasterById>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/trans-master/${id}`],
    queryFn: ({ signal }) => api.getTransMasterById(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};

export const useUpdateTransMasterById = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.updateTransMasterById>>,
      TError,
      { id: number; data: TransMasterDto },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.updateTransMasterById>>,
  TError,
  { id: number; data: TransMasterDto },
  TContext
> => {
  return useMutation({
    mutationFn: ({ id, data }) => api.updateTransMasterById(id, data, options?.request),
    ...options?.mutation,
  });
};

export const useDeleteTransMasterById = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.deleteTransMasterById>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.deleteTransMasterById>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation({
    mutationFn: ({ id }) => api.deleteTransMasterById(id, options?.request),
    ...options?.mutation,
  });
};

export const useUpdateTransMasterAction = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.updateTransMasterAction>>,
      TError,
      { id: number; action: string; data: unknown },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.updateTransMasterAction>>,
  TError,
  { id: number; action: string; data: unknown },
  TContext
> => {
  return useMutation({
    mutationFn: ({ id, action, data }) =>
      api.updateTransMasterAction(id, action, data, options?.request),
    ...options?.mutation,
  });
};

// Supplement Data Hooks
export const useGetAllSupplementData = <
  TData = Awaited<ReturnType<typeof api.getAllSupplementData>>,
  TError = ErrorType<unknown>
>(
  params?: GetAllSupplementDataParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getAllSupplementData>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/supp-data`, params],
    queryFn: ({ signal }) => api.getAllSupplementData(params, options?.request, signal),
    staleTime: 0,
    gcTime: 0,
    ...options?.query,
  });
};

export const useUpdateSupplementData = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.updateSupplementData>>,
      TError,
      { data: SupplementDataDto },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.updateSupplementData>>,
  TError,
  { data: SupplementDataDto },
  TContext
> => {
  return useMutation({
    mutationFn: ({ data }) => api.updateSupplementData(data, options?.request),
    ...options?.mutation,
  });
};

export const useCreateSupplementData = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.createSupplementData>>,
      TError,
      { data: SupplementDataDto },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.createSupplementData>>,
  TError,
  { data: SupplementDataDto },
  TContext
> => {
  return useMutation({
    mutationFn: ({ data }) => api.createSupplementData(data, options?.request),
    ...options?.mutation,
  });
};

export const useDeleteSupplementData = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.deleteSupplementData>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.deleteSupplementData>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation({
    mutationFn: ({ id }) => api.deleteSupplementData(id, options?.request),
    ...options?.mutation,
  });
};

export const useGetSupplementTypes = <
  TData = Awaited<ReturnType<typeof api.getSupplementTypes>>,
  TError = ErrorType<unknown>
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getSupplementTypes>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/supp-data/types`],
    queryFn: ({ signal }) => api.getSupplementTypes(options?.request, signal),
    ...options?.query,
  });
};

// Campaign Hooks
export const useGetAllCampaigns = <
  TData = Awaited<ReturnType<typeof api.getAllCampaigns>>,
  TError = ErrorType<unknown>
>(
  params?: GetAllCampaignsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getAllCampaigns>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/campaign`, params],
    queryFn: ({ signal }) => api.getAllCampaigns(params, options?.request, signal),
    ...options?.query,
  });
};

export const useGetCampaignById = <
  TData = Awaited<ReturnType<typeof api.getCampaignById>>,
  TError = ErrorType<unknown>
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getCampaignById>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/campaign/${id}`],
    queryFn: ({ signal }) => api.getCampaignById(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};

export const useUpdateCampaign = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.updateCampaign>>,
      TError,
      { data: CampaignRequestDto },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.updateCampaign>>,
  TError,
  { data: CampaignRequestDto },
  TContext
> => {
  return useMutation({
    mutationFn: ({ data }) => api.updateCampaign(data, options?.request),
    ...options?.mutation,
  });
};

export const useCreateCampaign = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.createCampaign>>,
      TError,
      { data: CampaignRequestDto },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.createCampaign>>,
  TError,
  { data: CampaignRequestDto },
  TContext
> => {
  return useMutation({
    mutationFn: ({ data }) => api.createCampaign(data, options?.request),
    ...options?.mutation,
  });
};

export const useDeleteCampaign = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.deleteCampaign>>,
      TError,
      { id: number },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.deleteCampaign>>,
  TError,
  { id: number },
  TContext
> => {
  return useMutation({
    mutationFn: ({ id }) => api.deleteCampaign(id, options?.request),
    ...options?.mutation,
  });
};

// Trans Master Filter Hook
export const useFilterTransMaster = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.filterTransMaster>>,
      TError,
      { data: import("./types").TransMasterFilter },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.filterTransMaster>>,
  TError,
  { data: import("./types").TransMasterFilter },
  TContext
> => {
  return useMutation({
    mutationFn: ({ data }) => api.filterTransMaster(data, options?.request),
    ...options?.mutation,
  });
};

// Sender Persona Hooks
export const useGetAllSenderPersonas = <
  TData = Awaited<ReturnType<typeof api.getAllSenderPersonas>>,
  TError = ErrorType<unknown>
>(
  params?: { name?: string; agentType?: string },
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getAllSenderPersonas>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/sender-persona`, params],
    queryFn: ({ signal }) => api.getAllSenderPersonas(params, options?.request, signal),
    enabled: !!params?.agentType, // Only run query when agentType is provided
    staleTime: 0,
    gcTime: 0,
    ...options?.query,
  });
};

// LLM Model Hooks
export const useGetAllLlmModels = <
  TData = Awaited<ReturnType<typeof api.getAllLlmModels>>,
  TError = ErrorType<unknown>
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getAllLlmModels>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/llm-models`],
    queryFn: ({ signal }) => api.getAllLlmModels(options?.request, signal),
    staleTime: 0,
    gcTime: 0,
    ...options?.query,
  });
};

// Analytics Hooks
export const useGetTransactionTracking = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.getTransactionTracking>>,
      TError,
      {
        agentType: string;
        fromDate?: string;
        toDate?: string;
        correlationId?: string;
        externalCd?: string;
        orderBy?: string;
        sortDirection?: string;
        minResponseTime?: number;
      },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.getTransactionTracking>>,
  TError,
  {
    agentType: string;
    fromDate?: string;
    toDate?: string;
    correlationId?: string;
    externalCd?: string;
    orderBy?: string;
    sortDirection?: string;
    minResponseTime?: number;
  },
  TContext
> => {
  return useMutation({
    mutationFn: (data) => api.getTransactionTracking(data, options?.request),
    ...options?.mutation,
  });
};

export const useExportTransactionHistoryCSV = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.exportTransactionHistoryCSV>>,
      TError,
      {
        agentType: string;
        fromDate?: string;
        toDate?: string;
        correlationId?: string;
        externalCd?: string;
        orderBy?: string;
        sortDirection?: string;
        minResponseTime?: number;
      },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.exportTransactionHistoryCSV>>,
  TError,
  {
    agentType: string;
    fromDate?: string;
    toDate?: string;
    correlationId?: string;
    externalCd?: string;
    orderBy?: string;
    sortDirection?: string;
    minResponseTime?: number;
  },
  TContext
> => {
  return useMutation({
    mutationFn: (data) => api.exportTransactionHistoryCSV(data, options?.request),
    ...options?.mutation,
  });
};

// Telemetry Hooks
export const useGetTransactionTelemetry = <
  TData = Awaited<ReturnType<typeof api.getTransactionTelemetry>>,
  TError = ErrorType<unknown>
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getTransactionTelemetry>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/analytics/transaction-history/telemetry/${id}`],
    queryFn: ({ signal }) => api.getTransactionTelemetry(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};

// Conversation Hooks
export const useGetTransactionConversation = <
  TData = Awaited<ReturnType<typeof api.getTransactionConversation>>,
  TError = ErrorType<unknown>
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getTransactionConversation>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/analytics/transaction-history/conversation/${id}`],
    queryFn: ({ signal }) => api.getTransactionConversation(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};

// Failure Transactions Hooks
export const useGetFailureTransactions = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.getFailureTransactions>>,
      TError,
      {
        agentType: string;
        transType?: string;
        correlationId?: string;
        fromDate?: string;
        toDate?: string;
        status?: string[];
      },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.getFailureTransactions>>,
  TError,
  {
    agentType: string;
    transType?: string;
    correlationId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string[];
  },
  TContext
> => {
  return useMutation({
    mutationFn: (data) => api.getFailureTransactions(data, options?.request),
    ...options?.mutation,
  });
};

export const useGetFailureTransactionPayload = <
  TData = Awaited<ReturnType<typeof api.getFailureTransactionPayload>>,
  TError = ErrorType<unknown>
>(
  id: number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getFailureTransactionPayload>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: [`/api/user/analytics/transaction-retry-queue/payload/${id}`],
    queryFn: ({ signal }) => api.getFailureTransactionPayload(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};

export const useExportFailureTransactionsCSV = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.exportFailureTransactionsCSV>>,
      TError,
      {
        agentType: string;
        transType?: string;
        correlationId?: string;
        fromDate?: string;
        toDate?: string;
        status?: string[];
      },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.exportFailureTransactionsCSV>>,
  TError,
  {
    agentType: string;
    transType?: string;
    correlationId?: string;
    fromDate?: string;
    toDate?: string;
    status?: string[];
  },
  TContext
> => {
  return useMutation({
    mutationFn: (data) => api.exportFailureTransactionsCSV(data, options?.request),
    ...options?.mutation,
  });
};

export const useRetryFailureTransaction = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.retryFailureTransaction>>,
      TError,
      number,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.retryFailureTransaction>>,
  TError,
  number,
  TContext
> => {
  return useMutation({
    mutationFn: (id) => api.retryFailureTransaction(id, options?.request),
    ...options?.mutation,
  });
};

// Auth Hooks
export const useExchangeToken = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof api.exchangeToken>>,
      TError,
      void,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseMutationResult<
  Awaited<ReturnType<typeof api.exchangeToken>>,
  TError,
  void,
  TContext
> => {
  return useMutation({
    mutationFn: () => api.exchangeToken(options?.request),
    ...options?.mutation,
  });
};

// Knowledge Base Query Key Helpers
export const getConfigDetailsQueryKey = (key: string, agentType: string) => {
  return [`${API_CONFIG.ENDPOINTS.INTEGRATION_CONFIG}/key/${key}/type/${agentType}`] as const;
};

export const getKbInfoQueryKey = (params?: { agentType?: string; kbType?: string }) => {
  return [`${API_CONFIG.ENDPOINTS.KNOWLEDGE_BASE_INFO}/search`, ...(params ? [params] : [])] as const;
};

export const getKbInfoByIdQueryKey = (id?: string | number) => {
  return [`${API_CONFIG.ENDPOINTS.KNOWLEDGE_BASE_INFO}/id`, id] as const;
};

// Knowledge Base Hooks
export const useGetConfigDetails = <
  TData = Awaited<ReturnType<typeof api.getConfigDetails>>,
  TError = ErrorType<unknown>
>(
  key: string,
  agentType: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getConfigDetails>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: getConfigDetailsQueryKey(key, agentType),
    queryFn: ({ signal }) => api.getConfigDetails(key, agentType, options?.request, signal),
    enabled: !!key && !!agentType,
    ...options?.query,
  });
};

export const useGetKbInfo = <
  TData = Awaited<ReturnType<typeof api.getKbInfo>>,
  TError = ErrorType<unknown>
>(
  filter: { agentType: string; kbType: string },
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getKbInfo>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: getKbInfoQueryKey(filter),
    queryFn: ({ signal }) => api.getKbInfo(filter, options?.request, signal),
    enabled: !!filter.agentType && !!filter.kbType,
    ...options?.query,
  });
};

export const useGetKbInfoById = <
  TData = Awaited<ReturnType<typeof api.getKbInfoById>>,
  TError = ErrorType<unknown>
>(
  id: string | number,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof api.getKbInfoById>>, TError, TData>>;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> => {
  return useQuery({
    queryKey: getKbInfoByIdQueryKey(id),
    queryFn: ({ signal }) => api.getKbInfoById(id, options?.request, signal),
    enabled: !!id,
    ...options?.query,
  });
};